/**
 * Microphone permission prompts — always via getUserMedia (MediaRecorder + server STT on all browsers).
 * getUserMedia is a permission probe only: stop tracks before recording so concurrent streams
 * do not starve capture on iOS Safari / Android Chrome.
 */

function getSpeechRecognitionCtor() {
  if (typeof window === 'undefined') return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

/** Safari / WebKit (macOS, iOS, iPadOS). Excludes Chrome-on-iOS (CriOS) etc. */
export function isSafari() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/CriOS|FxiOS|EdgiOS|OPiOS/i.test(ua)) return false;
  return /Safari/i.test(ua) && !/Chrome|Chromium|Android/i.test(ua);
}

/** iPhone, iPad, and iPod (includes iPadOS desktop UA). */
export function isIOSDevice() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/iPhone|iPad|iPod/i.test(ua)) return true;
  return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
}

/** Phones and tablets (iOS, iPadOS, Android). */
export function isMobileBrowser() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/Android|iPhone|iPad|iPod/i.test(ua)) return true;
  return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
}

/** Whether a MediaStream still has at least one live audio track. */
export function isStreamLive(stream) {
  if (!stream) return false;
  const tracks = stream.getAudioTracks();
  return tracks.length > 0 && tracks.some((track) => track.readyState === 'live');
}

/** Chromium-based browsers (Chrome, Edge, Opera, iOS Chrome, etc.). */
export function isChromeFamilyBrowser() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /CriOS|Chromium|Chrome|Edg\/|OPR\/|SamsungBrowser/i.test(ua);
}

/** True when this browser can record audio for POST /api/stt. */
export function preferRecorderSpeechInput() {
  return (
    typeof MediaRecorder !== 'undefined' &&
    Boolean(navigator.mediaDevices?.getUserMedia)
  );
}

/** True when stop() should upload recorded audio to POST /api/stt. */
export function usesServerSpeechToText() {
  return preferRecorderSpeechInput();
}

/** All supported browsers use getUserMedia for the mic permission prompt. */
export function shouldUseGetUserMediaForPermission() {
  return preferRecorderSpeechInput();
}

function hasOsMicrophoneBlockSignal(err) {
  const msg = String(err?.message || '').toLowerCase();
  if (/denied by system|system permission|not allowed by the user agent|permission denied by system/i.test(msg)) {
    return true;
  }
  const name = err?.name || '';
  if (name !== 'NotReadableError' && name !== 'TrackStartError') {
    return false;
  }
  if (/in use|busy|already in use/i.test(msg)) {
    return false;
  }
  return true;
}

/** Normalized mic error codes from getUserMedia / permission flows. */
export function classifyMicrophoneError(errOrCode) {
  if (typeof errOrCode === 'string') {
    return errOrCode || 'failed';
  }
  const name = errOrCode?.name || '';
  if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
    return 'denied';
  }
  if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
    return 'not-found';
  }
  if (name === 'SecurityError') {
    return 'insecure';
  }
  if (hasOsMicrophoneBlockSignal(errOrCode)) {
    return 'system-blocked';
  }
  if (name === 'NotReadableError' || name === 'TrackStartError') {
    return 'not-readable';
  }
  return 'failed';
}

/** i18n key for a mic permission / capture error code. */
export function micPermissionErrorTranslationKey(error) {
  switch (classifyMicrophoneError(error)) {
    case 'denied':
      return 'errors.micPermissionDenied';
    case 'not-found':
      return 'errors.micNotFound';
    case 'insecure':
      return 'errors.micInsecure';
    case 'system-blocked':
      return 'errors.micSystemBlocked';
    case 'not-readable':
      return 'errors.micNotReadable';
    case 'unsupported':
      return 'app.voiceInputUnsupported';
    default:
      return 'errors.voiceInputFailed';
  }
}

/** Error codes passed to useSpeechInput onError from getUserMedia failures. */
export function speechInputErrorCodeFromGetUserMedia(err) {
  switch (classifyMicrophoneError(err)) {
    case 'denied':
      return 'not-allowed';
    case 'not-found':
      return 'audio-capture';
    case 'insecure':
      return 'insecure';
    case 'system-blocked':
      return 'system-blocked';
    case 'not-readable':
      return 'not-readable';
    default:
      return 'audio-capture';
  }
}

function mapGetUserMediaError(err) {
  return { granted: false, error: classifyMicrophoneError(err) };
}

/** Shared WebRTC audio processing — explicit so browsers don't fall back to raw mic. */
const PROCESSED_AUDIO = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
};

export function getUserMediaConstraints() {
  // iOS play-and-record: echo cancellation / AGC shift speaker gain and duck TTS output.
  // Noise suppression alone reduces ambient hum without those route side effects.
  if (isIOSDevice()) {
    return {
      audio: {
        echoCancellation: false,
        noiseSuppression: true,
        autoGainControl: false,
      },
    };
  }
  return { audio: PROCESSED_AUDIO };
}

/**
 * Begin getUserMedia in the same synchronous turn as the user's tap (required on mobile).
 * @returns {Promise<MediaStream> | null}
 */
export function beginGetUserMediaPermission() {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
    return null;
  }
  if (typeof window !== 'undefined' && window.isSecureContext === false) {
    return null;
  }
  return navigator.mediaDevices.getUserMedia(getUserMediaConstraints());
}

let activeWarmupStream = null;

/** Keep getUserMedia tracks open while SpeechRecognition is active (legacy; prefer probe-only). */
export function retainMicrophoneWarmupStream(stream) {
  releaseMicrophoneWarmupStream();
  activeWarmupStream = stream;
}

export function releaseMicrophoneWarmupStream() {
  if (!activeWarmupStream) return;
  for (const track of activeWarmupStream.getTracks()) {
    track.stop();
  }
  activeWarmupStream = null;
}

/**
 * Whether the page already holds microphone permission. Once true we must NOT open a
 * getUserMedia stream alongside SpeechRecognition: on Android Chrome and iOS Safari the
 * recognizer opens its own microphone, and a concurrent getUserMedia stream starves it of
 * audio (no results on repeat dictation / Android). getUserMedia is only a permission probe.
 *
 * iOS Safari cannot query the Permissions API, so sessionStorage backs the in-memory flag
 * for the tab session after the first successful grant.
 */
const MIC_PERMISSION_SESSION_KEY = 'aotf_mic_granted';
let microphonePermissionGranted = false;

function hydrateMicrophonePermissionFromSession() {
  if (microphonePermissionGranted) return;
  try {
    if (sessionStorage.getItem(MIC_PERMISSION_SESSION_KEY) === '1') {
      microphonePermissionGranted = true;
    }
  } catch {
    /* ignore */
  }
}

export function markMicrophonePermissionGranted() {
  microphonePermissionGranted = true;
  try {
    sessionStorage.setItem(MIC_PERMISSION_SESSION_KEY, '1');
  } catch {
    /* ignore */
  }
}

export function isMicrophonePermissionKnownGranted() {
  hydrateMicrophonePermissionFromSession();
  return microphonePermissionGranted;
}

/** Release warmup stream + clear cached permission (mic disabled, new artefact, etc.). */
export function releaseMicrophoneAccess() {
  microphonePermissionGranted = false;
  try {
    sessionStorage.removeItem(MIC_PERMISSION_SESSION_KEY);
  } catch {
    /* ignore */
  }
  releaseMicrophoneWarmupStream();
}

/** Clear cached permission + warmup stream (e.g. after landing / new artefact). */
export function resetMicrophonePermissionState() {
  releaseMicrophoneAccess();
}

/**
 * Sync the cached permission flag from the Permissions API when available (Android Chrome,
 * desktop). iOS Safari does not support querying 'microphone', so the flag stays false there
 * until the first getUserMedia probe succeeds. Safe to call on mount.
 */
export function refreshMicrophonePermissionState() {
  hydrateMicrophonePermissionFromSession();
  if (typeof navigator === 'undefined' || !navigator.permissions?.query) {
    return;
  }
  try {
    navigator.permissions
      .query({ name: 'microphone' })
      .then((status) => {
        if (status.state === 'granted') {
          markMicrophonePermissionGranted();
        }
        status.onchange = () => {
          if (status.state === 'granted') {
            markMicrophonePermissionGranted();
          } else {
            microphonePermissionGranted = false;
          }
        };
      })
      .catch(() => {
        /* not queryable (e.g. iOS Safari) — leave flag as-is */
      });
  } catch {
    /* ignore */
  }
}

/**
 * @param {Promise<MediaStream>} streamPromise
 * @param {{ keepStream?: boolean }} [options]
 * @returns {Promise<{ granted: boolean, error?: string }>}
 */
export async function finishGetUserMediaPermission(streamPromise, options = {}) {
  const { keepStream = false } = options;
  try {
    const stream = await streamPromise;
    if (keepStream) {
      retainMicrophoneWarmupStream(stream);
    } else {
      for (const track of stream.getTracks()) {
        track.stop();
      }
    }
    markMicrophonePermissionGranted();
    return { granted: true };
  } catch (err) {
    return mapGetUserMediaError(err);
  }
}

/**
 * Desktop: ask via SpeechRecognition.start (native allow/deny dialog).
 * @param {string} locale BCP-47 locale (e.g. en-US, el-GR)
 * @returns {Promise<{ granted: boolean, error?: string }>}
 */
export function requestMicrophoneViaSpeechRecognition(locale) {
  const Ctor = getSpeechRecognitionCtor();
  if (!Ctor) {
    return Promise.resolve({ granted: false, error: 'unsupported' });
  }
  if (typeof window !== 'undefined' && window.isSecureContext === false) {
    return Promise.resolve({ granted: false, error: 'insecure' });
  }

  return new Promise((resolve) => {
    const rec = new Ctor();
    let settled = false;

    const finish = (result) => {
      if (settled) return;
      settled = true;
      try {
        rec.abort();
      } catch {
        try {
          rec.stop();
        } catch {
          /* ignore */
        }
      }
      resolve(result);
    };

    rec.lang = locale;
    rec.continuous = false;
    rec.interimResults = false;
    rec.maxAlternatives = 1;

    rec.onstart = () => {
      markMicrophonePermissionGranted();
      finish({ granted: true });
    };

    rec.onerror = (event) => {
      const code = event?.error || '';
      if (code === 'aborted') return;
      if (code === 'not-allowed' || code === 'service-not-allowed') {
        finish({ granted: false, error: 'denied' });
        return;
      }
      if (code === 'audio-capture') {
        finish({ granted: false, error: 'not-found' });
        return;
      }
      finish({ granted: false, error: code || 'failed' });
    };

    rec.onend = () => {
      if (!settled) finish({ granted: false, error: 'cancelled' });
    };

    try {
      rec.start();
    } catch (err) {
      finish({ granted: false, error: err?.name || 'start-failed' });
    }
  });
}

/**
 * Start the permission flow synchronously inside a click/tap handler.
 * Returns a promise that resolves when the user allows or denies.
 *
 * @param {string} locale BCP-47 locale for desktop speech path
 * @returns {Promise<{ granted: boolean, error?: string }>}
 */
export function beginMicrophonePermissionPrompt(locale) {
  if (shouldUseGetUserMediaForPermission()) {
    const streamPromise = beginGetUserMediaPermission();
    if (!streamPromise) {
      if (typeof window !== 'undefined' && window.isSecureContext === false) {
        return Promise.resolve({ granted: false, error: 'insecure' });
      }
      return Promise.resolve({ granted: false, error: 'unsupported' });
    }
    return finishGetUserMediaPermission(streamPromise);
  }
  return requestMicrophoneViaSpeechRecognition(locale);
}
